import { useEffect, useState } from "react";
import {
    Plus,
    X,
    Edit3,
    Trash2,
    Search,
    Upload,
    Download,
    ChevronLeft,
    ChevronRight,
    Truck,
    AlertCircle,
    CheckCircle,
    FileText,
} from "lucide-react";
import Swal from "sweetalert2";
import API_BASE_URL from "../config";

export default function TruckPage({ setCurrentPage }) {
    const [trucks, setTrucks] = useState([]);
    const [filtered, setFiltered] = useState([]);

    const [openCreate, setOpenCreate] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [selectedTruck, setSelectedTruck] = useState(null);

    const [truckPlate, setTruckPlate] = useState("");
    const [model, setModel] = useState("");
    const [level, setLevel] = useState("");

    const [search, setSearch] = useState("");

    // Import Modal States
    const [showImportModal, setShowImportModal] = useState(false);
    const [importData, setImportData] = useState([]);
    const [importMode, setImportMode] = useState("new"); // "new" or "update"

    // Pagination
    const [page, setPage] = useState(1);
    const perPage = 8;
    const totalPages = Math.ceil(filtered.length / perPage);
    const paginated = filtered.slice((page - 1) * perPage, page * perPage);

    // ---------------- API FUNCTIONS ---------------- //

    const fetchTrucks = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}api/Truck`);
            if (!res.ok) throw new Error("Failed to fetch trucks");

            const data = await res.json();

            const mapped = data.map((t) => ({
                id: t.id,
                truckPlate: t.truckPlate || t.TruckPlate,
                model: t.model || t.Model,
                level: t.level || t.Level,
            }));

            setTrucks(mapped);
            setFiltered(mapped);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Failed to fetch trucks", "error");
        }
    };

    const createTruck = async () => {
        if (!truckPlate || !model || !level) {
            Swal.fire("Oops!", "Please fill in all fields", "warning");
            return;
        }

        const body = {
            TruckPlate: truckPlate,
            Model: model,
            Level: level,
        };

        try {
            const res = await fetch(`${API_BASE_URL}api/Truck`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Error response:", errorText);
                throw new Error("Failed to create truck");
            }

            setOpenCreate(false);
            clearForm();
            fetchTrucks();
            Swal.fire("Success!", "Truck created successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const updateTruck = async () => {
        if (!truckPlate || !model || !level) {
            Swal.fire("Oops!", "Please fill in all fields", "warning");
            return;
        }

        const body = {
            TruckPlate: truckPlate,
            Model: model,
            Level: level,
        };

        try {
            const res = await fetch(`${API_BASE_URL}api/Truck/${selectedTruck.id}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Error response:", errorText);
                throw new Error("Failed to update truck");
            }

            setOpenEdit(false);
            setSelectedTruck(null);
            clearForm();
            fetchTrucks();
            Swal.fire("Updated!", "Truck updated successfully", "success");
        } catch (err) {
            console.error(err);
            Swal.fire("Error", err.message, "error");
        }
    };

    const deleteTruck = async (truck) => {
        const result = await Swal.fire({
            title: `Are you sure?`,
            text: `You are about to delete ${truck.truckPlate}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE_URL}api/Truck/${truck.id}`, {
                    method: "DELETE",
                });
                if (!res.ok) throw new Error("Failed to delete truck");
                fetchTrucks();
                Swal.fire("Deleted!", "Truck has been deleted.", "success");
            } catch (err) {
                console.error(err);
                Swal.fire("Error", err.message, "error");
            }
        }
    };

    // EXPORT CSV
    const exportCSV = () => {
        const header = "ID,TruckPlate,Model,Level\n";
        const rows = trucks
            .map(
                (t) =>
                    `${t.id},"${t.truckPlate ?? ""}","${t.model ?? ""}","${t.level ?? ""}"`
            )
            .join("\n");

        const blob = new Blob([header + rows], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "trucks.csv";
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    // IMPORT CSV - STEP 1: Parse and Show Modal
    const handleImportFile = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const text = await file.text();
            const lines = text.split("\n");
            const header = lines[0].toLowerCase();

            // Validate header
            if (!header.includes("truckplate") || !header.includes("model") || !header.includes("level")) {
                Swal.fire("Error", "Invalid CSV format. Expected columns: ID, TruckPlate, Model, Level", "error");
                return;
            }

            const dataLines = lines.slice(1);
            const parsed = dataLines
                .map((line) => line.split(","))
                .filter((cols) => cols.length >= 4 && cols[1]?.trim() && cols[2]?.trim())
                .map((cols) => ({
                    truckPlate: cols[1]?.trim().replace(/^"|"$/g, ""),
                    model: cols[2]?.trim().replace(/^"|"$/g, ""),
                    level: cols[3]?.trim().replace(/^"|"$/g, ""),
                }));

            if (parsed.length === 0) {
                Swal.fire("Error", "No valid data found in CSV file", "error");
                return;
            }

            // Check for duplicates
            const dataWithStatus = parsed.map((item) => {
                const existing = trucks.find(
                    (t) =>
                        t.truckPlate?.toLowerCase() === item.truckPlate?.toLowerCase()
                );
                return {
                    ...item,
                    isDuplicate: !!existing,
                    existingId: existing?.id,
                };
            });

            setImportData(dataWithStatus);
            setShowImportModal(true);
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to parse CSV file", "error");
        }

        event.target.value = "";
    };

    // IMPORT CSV - STEP 2: Process Import
    const processImport = async () => {
        setShowImportModal(false);

        try {
            const requests = importData
                .filter((item) => !(item.isDuplicate && importMode === "new"))
                .map((item) => {
                    const body = {
                        TruckPlate: item.truckPlate,
                        Model: item.model,
                        Level: item.level,
                    };

                    if (item.isDuplicate && importMode === "update") {
                        return fetch(`${API_BASE_URL}api/Truck/${item.existingId}`, {
                            method: "PUT",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(body),
                        });
                    } else {
                        return fetch(`${API_BASE_URL}api/Truck`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify(body),
                        });
                    }
                });

            const results = await Promise.allSettled(requests);
            const successCount = results.filter((r) => r.status === "fulfilled").length;
            const failCount = results.filter((r) => r.status === "rejected").length;

            fetchTrucks();
            setImportData([]);
            setImportMode("new");

            Swal.fire({
                icon: "success",
                title: "Import Complete!",
                html: `
                    <p class="text-lg mb-2"><strong>${successCount}</strong> trucks processed successfully</p>
                    ${failCount > 0 ? `<p class="text-red-600">${failCount} failed</p>` : ""}
                `,
            });
        } catch (err) {
            console.error(err);
            Swal.fire("Error", "Failed to import trucks", "error");
        }
    };

    // ---------------- USE EFFECTS ---------------- //

    useEffect(() => {
        fetchTrucks();
    }, []);

    useEffect(() => {
        if (!search.trim()) {
            setFiltered(trucks);
        } else {
            const term = search.toLowerCase();
            setFiltered(
                trucks.filter(
                    (t) =>
                        t.truckPlate?.toLowerCase().includes(term) ||
                        t.model?.toLowerCase().includes(term) ||
                        t.level?.toLowerCase().includes(term)
                )
            );
        }
        setPage(1);
    }, [search, trucks]);

    // ---------------- HELPERS ---------------- //

    const clearForm = () => {
        setTruckPlate("");
        setModel("");
        setLevel("");
    };

    const openEditModal = (t) => {
        setSelectedTruck(t);
        setTruckPlate(t.truckPlate);
        setModel(t.model);
        setLevel(t.level);
        setOpenEdit(true);
    };

    const duplicateCount = importData.filter((d) => d.isDuplicate).length;
    const newCount = importData.filter((d) => !d.isDuplicate).length;

    // ---------------- RETURN UI ---------------- //
    return (
        <div className="min-h-screen from-gray-50 to-gray-100 p-8">
            {/* BACK BUTTON */}
            <button
                onClick={() => setCurrentPage("referencess")}
                className="mb-2 flex items-center gap-5 text-indigo-600 hover:text-indigo-800 font-semibold transition-colors group ml-8"
            >
                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                Back to References
            </button>

            {/* HEADER */}
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3">
                            <Truck className="text-indigo-600" size={36} />
                            Trucks
                        </h1>
                        <p className="text-gray-600 mt-2">Manage your fleet of trucks</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportCSV}
                            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-3 rounded-xl hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Download size={18} /> Export
                        </button>

                        <label className="flex items-center gap-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-5 py-3 rounded-xl hover:from-yellow-600 hover:to-yellow-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
                            <Upload size={18} /> Import
                            <input
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={handleImportFile}
                            />
                        </label>

                        <button
                            onClick={() => setOpenCreate(true)}
                            className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-6 py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                        >
                            <Plus size={20} /> Add Truck
                        </button>
                    </div>
                </div>
            </div>

            {/* SEARCH BAR */}
            <div className="mb-6">
                <div className="relative max-w-md">
                    <Search size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by plate, model, or level..."
                        className="w-full pl-12 pr-4 py-3 bg-white rounded-xl border-2 border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                <th className="px-6 py-4 text-left text-sm font-semibold">ID</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Truck Plate</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Model</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Level</th>
                                <th className="px-6 py-4 text-center text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {paginated.length > 0 ? (
                                paginated.map((truck, index) => (
                                    <tr
                                        key={truck.id || `truck-${index}`}
                                        className="hover:bg-indigo-50 transition-colors"
                                    >
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-indigo-100 text-indigo-800 font-semibold text-sm">
                                                {truck.id}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                {truck.truckPlate}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                                                    <Truck size={18} />
                                                </div>
                                                <span className="font-semibold text-gray-800">
                                                    {truck.model}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {truck.level}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(truck)}
                                                    className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Edit"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => deleteTruck(truck)}
                                                    className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors shadow-sm hover:shadow-md"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3 text-gray-400">
                                            <Truck size={48} />
                                            <p className="text-lg font-medium">No trucks found</p>
                                            <p className="text-sm">
                                                Try adjusting your search or add a new truck
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                {paginated.length > 0 && (
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                                Showing {(page - 1) * perPage + 1} to{" "}
                                {Math.min(page * perPage, filtered.length)} of {filtered.length} trucks
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    disabled={page <= 1}
                                    onClick={() => setPage(page - 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span className="text-sm font-semibold text-gray-700">
                                    Page {page} of {totalPages || 1}
                                </span>
                                <button
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(page + 1)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* CREATE/EDIT MODAL */}
            {(openCreate || openEdit) && (
                <Modal
                    title={openCreate ? "Add New Truck" : "Edit Truck"}
                    close={() => {
                        setOpenCreate(false);
                        setOpenEdit(false);
                        setSelectedTruck(null);
                        clearForm();
                    }}
                >
                    <div className="space-y-4">
                        <Input
                            label="Truck Plate*"
                            value={truckPlate}
                            onChange={setTruckPlate}
                            placeholder="e.g. ABC-1234"
                            required
                        />
                        <Input
                            label="Model*"
                            value={model}
                            onChange={setModel}
                            placeholder="e.g. Isuzu Elf"
                            required
                        />
                        <Input
                            label="Level*"
                            value={level}
                            onChange={setLevel}
                            placeholder="e.g. Heavy / Medium / Light"
                            required
                        />
                    </div>

                    <button
                        onClick={openCreate ? createTruck : updateTruck}
                        className="mt-6 w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-3 rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                    >
                        {openCreate ? "Add Truck" : "Update Truck"}
                    </button>
                </Modal>
            )}

            {/* IMPORT PREVIEW MODAL */}
            {showImportModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-6">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h2 className="text-3xl font-bold flex items-center gap-3">
                                        <FileText size={32} />
                                        Import Preview
                                    </h2>
                                    <p className="text-indigo-100 mt-2">
                                        Review data before importing • {importData.length} total records
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowImportModal(false);
                                        setImportData([]);
                                        setImportMode("new");
                                    }}
                                    className="text-white hover:bg-white/20 rounded-lg p-2 transition"
                                >
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-green-100 rounded-full p-3">
                                        <CheckCircle className="text-green-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{newCount}</p>
                                        <p className="text-sm text-gray-600">New Records</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
                                    <div className="bg-red-100 rounded-full p-3">
                                        <AlertCircle className="text-red-600" size={24} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-gray-800">{duplicateCount}</p>
                                        <p className="text-sm text-gray-600">Duplicates Found</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mode Selection */}
                        {duplicateCount > 0 && (
                            <div className="px-8 py-4 bg-yellow-50 border-b border-yellow-200">
                                <p className="text-sm font-semibold text-yellow-800 mb-3">
                                    What would you like to do with duplicate records?
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setImportMode("new")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
                                            importMode === "new"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                        }`}
                                    >
                                        Skip Duplicates (Import {newCount} new only)
                                    </button>
                                    <button
                                        onClick={() => setImportMode("update")}
                                        className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all ${
                                            importMode === "update"
                                                ? "bg-indigo-600 text-white shadow-lg"
                                                : "bg-white text-gray-700 border-2 border-gray-300 hover:border-indigo-300"
                                        }`}
                                    >
                                        Update Existing (Import all {importData.length})
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Data Table */}
                        <div className="flex-1 overflow-y-auto px-8 py-4">
                            <table className="w-full">
                                <thead className="sticky top-0 bg-gray-100 border-b-2 border-gray-300">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Truck Plate</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Model</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Level</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {importData.map((item, index) => (
                                        <tr
                                            key={index}
                                            className={`${
                                                item.isDuplicate
                                                    ? "bg-red-50 hover:bg-red-100"
                                                    : "bg-white hover:bg-gray-50"
                                            } transition-colors`}
                                        >
                                            <td className="px-4 py-3">
                                                {item.isDuplicate ? (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                                                        <AlertCircle size={14} />
                                                        Duplicate
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                        <CheckCircle size={14} />
                                                        New
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`font-medium ${item.isDuplicate ? "text-red-900" : "text-gray-800"}`}>
                                                    {item.truckPlate}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`font-medium ${item.isDuplicate ? "text-red-900" : "text-gray-800"}`}>
                                                    {item.model}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`font-medium ${item.isDuplicate ? "text-red-900" : "text-gray-800"}`}>
                                                    {item.level}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {item.isDuplicate ? (
                                                    <span className="text-xs text-red-600 font-semibold">
                                                        {importMode === "update" ? "Will Update" : "Will Skip"}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-green-600 font-semibold">
                                                        Will Create
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer Actions */}
                        <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                            <button
                                onClick={() => {
                                    setShowImportModal(false);
                                    setImportData([]);
                                    setImportMode("new");
                                }}
                                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={processImport}
                                className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-indigo-800 shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                            >
                                {importMode === "update"
                                    ? `Import All (${importData.length} records)`
                                    : `Import New Only (${newCount} records)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Modal Component
function Modal({ title, close, children }) {
    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={close}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition"
                >
                    <X size={24} />
                </button>
                <h2 className="text-3xl font-bold text-gray-800 mb-8">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", placeholder = "", required = false }) {
    return (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                required={required}
                className="block w-full border-2 border-gray-300 rounded-lg shadow-sm px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none"
            />
        </div>
    );
}